/**
 * Servicio de equipo — lee desde Realtime Database, nodo `/team`.
 *
 * Estructura genérica esperada en Firebase RTDB:
 *
 *   /team/
 *     $memberId/
 *       name:        string   (requerido)
 *       role:        string   (requerido)
 *       bio:         string   (requerido)
 *       photoUrl:    string   (opcional — URL pública o link de Google Drive)
 *       linkedinUrl: string   (opcional — URL de LinkedIn)
 *       order:       number   (requerido — orden ascendente para mostrar)
 *       active:      boolean  (requerido — solo se muestran los `true`)
 *
 * Reglas en `database.rules.json`: `/team` con `.read: true` y `.write: false`.
 *
 * ## Fotos desde Google Drive
 * Puedes pegar el link de compartir de Drive directamente en `photoUrl`:
 *   https://drive.google.com/file/d/<ID>/view?usp=sharing
 * El servicio lo convierte automáticamente al endpoint `thumbnail`:
 *   https://drive.google.com/thumbnail?id=<ID>&sz=w800
 *
 * NOTA: el archivo de Drive debe estar compartido como "Cualquier persona con el
 * enlace puede verlo" para que funcione sin autenticación.
 */
import { ref, onValue, query, orderByChild } from 'firebase/database';

import type { TeamMember, TeamMemberSnapshot } from '../types/domain';

import { db } from './firebaseConfig';

export type TeamSubscriptionCallback = (members: TeamMember[], error: Error | null) => void;
export type Unsubscribe = () => void;

/**
 * Convierte un link de Google Drive compartido a una URL directa de imagen.
 *
 * Formatos soportados (cualquier variante `?usp=...` funciona):
 *   https://drive.google.com/file/d/<ID>/view?usp=sharing
 *   https://drive.google.com/file/d/<ID>/view?usp=drive_link
 *   https://drive.google.com/open?id=<ID>
 *
 * Se usa el endpoint `thumbnail` porque `uc?export=view` está bloqueado por
 * Google desde 2023 como `<img src>` (devuelve redirects o página de login).
 * `thumbnail` permite hotlink y acepta un parámetro `sz` (ej. `w800`, `s1200`).
 *
 * Si el link no corresponde a Google Drive se devuelve sin cambios.
 */
export function resolvePhotoUrl(raw: string): string {
  if (!raw) return raw;

  const toThumb = (id: string) =>
    `https://drive.google.com/thumbnail?id=${id}&sz=w800`;

  // Formato: /file/d/<ID>/view
  const fileMatch = raw.match(/\/file\/d\/([^/?]+)/);
  if (fileMatch) {
    return toThumb(fileMatch[1]);
  }

  // Formato: open?id=<ID>  o cualquier URL de Drive con ?id=
  const openMatch = raw.match(/[?&]id=([^&]+)/);
  if (openMatch && raw.includes('drive.google.com')) {
    return toThumb(openMatch[1]);
  }

  return raw;
}

/**
 * Normaliza un snapshot de Firebase a un array de miembros listo para renderear.
 *
 * - Tolera nodo vacío o ausente devolviendo `[]`.
 * - Solo incluye miembros con `active !== false` (por defecto activo).
 * - Ordena por `order` ascendente; los miembros sin `order` van al final.
 * - Asigna un `num` con padding (`01`, `02`, …) para la UI.
 * - Convierte automáticamente los links de Google Drive en `photoUrl`.
 */
export function normalizeTeamSnapshot(value: unknown): TeamMember[] {
  if (!value || typeof value !== 'object') return [];

  const record = value as Record<string, TeamMemberSnapshot>;

  const entries = Object.entries(record).map(([id, raw]) => ({
    id,
    name: raw?.name ?? '',
    role: raw?.role ?? '',
    bio: raw?.bio ?? '',
    photoUrl: resolvePhotoUrl(raw?.photoUrl ?? ''),
    linkedinUrl: raw?.linkedinUrl ?? '',
    order: typeof raw?.order === 'number' ? raw.order : Number.POSITIVE_INFINITY,
    active: raw?.active !== false,
  }));

  return entries
    .filter((m) => m.active && m.name)
    .sort((a, b) => a.order - b.order)
    .map((m, i) => ({ ...m, num: String(i + 1).padStart(2, '0') }));
}

/**
 * Se suscribe al nodo `/team` y notifica al callback cada vez que cambia.
 */
export function subscribeToTeamMembers(cb: TeamSubscriptionCallback): Unsubscribe {
  const teamRef = query(ref(db, '/team'), orderByChild('order'));
  const unsubscribe = onValue(
    teamRef,
    (snapshot) => {
      try {
        const members = normalizeTeamSnapshot(snapshot.val());
        cb(members, null);
      } catch (err) {
        cb([], err as Error);
      }
    },
    (err) => cb([], err)
  );
  return unsubscribe;
}
